import { ResourceClient, type HttpClient } from "@dl/web-core-js/api";

/**
 * The plant's orders: a part, a quantity, the component tree it needs, and the
 * stock it moves. A production order (FABRICATION) makes a product's
 * semi-finished set at Laval; its assembly orders (ASSEMBLY, one per version)
 * box the sets at Terrebonne. SIGM's projects arrive as production orders,
 * marked is_from_sigm; nexpim's own number from 900001.
 *
 * Issues and output are ledger movements pointing at the order, so there is no
 * issue resource here — they are read from the stock ledger, filtered by order.
 * Wire names match the Java `@JsonProperty`s.
 */

/**
 * Where an order is, in the plant's words. A production order: planned, in
 * production (RELEASED), produced, shipping (first truck gone), closed
 * (everything shipped). An assembly order: planned with it, firmed (sets
 * arrived), in progress, completed (on a shelf). A kanban or service lot:
 * released, produced, completed. Cancelled for any.
 */
export const PRODUCTION_ORDER_STATUSES = [
  "PLANNED",
  "FIRMED",
  "RELEASED",
  "IN_PROGRESS",
  "PRODUCED",
  "SHIPPING",
  "COMPLETED",
  "CLOSED",
  "CANCELLED",
] as const;
export type ProductionOrderStatus = (typeof PRODUCTION_ORDER_STATUSES)[number];

/** The statuses of an order that is on the floor somewhere. */
export const ON_THE_FLOOR: readonly ProductionOrderStatus[] = ["RELEASED", "PRODUCED", "SHIPPING", "FIRMED", "IN_PROGRESS"];

/** The statuses an order is finished in. */
export const FINISHED: readonly ProductionOrderStatus[] = ["COMPLETED", "CLOSED", "CANCELLED"];

export const PRODUCTION_ORDER_KINDS = ["FABRICATION", "ASSEMBLY", "KANBAN", "SERVICE"] as const;
/** FABRICATION: a product's semi-finished set, at Laval. ASSEMBLY: one version of it boxed, at Terrebonne. KANBAN: components to stock. SERVICE: parts service orders wait for. */
export type ProductionOrderKind = (typeof PRODUCTION_ORDER_KINDS)[number];

export interface ProductionOrderDTO {
  id: number;
  number: number;
  /** For an imported SIGM child project, the run it belongs to. */
  parent_production_order_id: number | null;
  /** Null only on a SERVICE lot, whose components are the parts. */
  part_id: number | null;
  part_number?: string | null;
  part_name?: string | null;
  stock_unit_code?: string | null;
  quantity: number;
  /** Computed: SIGM's figure before cutover plus the ledger. */
  quantity_produced?: number | null;
  /** Changed only through the order's actions, never by saving the header. */
  status: ProductionOrderStatus;
  warehouse_id: number;
  warehouse_code?: string | null;
  bom_id: number | null;
  production_pipeline_lot_id: number | null;
  name: string | null;
  start_date: string | null;
  due_date: string | null;
  particle_cut_date: string | null;
  mdf_cut_date: string | null;
  arrived_laval_date: string | null;
  /** SIGM's flag, imported only: the lot is at Terrebonne. nexpim lots follow status. */
  is_at_terrebonne: boolean;
  floor_note: string | null;
  floor_comment: string | null;
  note: string | null;
  material_count?: number;
  /** Stock materials still short of what the run needs. */
  material_open_count?: number;
  child_count?: number;
  released_at?: string | null;
  completed_at?: string | null;
  sigm_project_id?: number | null;
  kind: ProductionOrderKind;
  /** Floor priority, lowest first; null after every prioritised lot. */
  priority?: number | null;
  is_on_hold?: boolean;
  hold_reason?: string | null;
  produced_at?: string | null;
  /** A production order: when its first truck left Laval. */
  shipping_at?: string | null;
  /** An assembly order: the production order whose sets it boxes. */
  fabrication_order_id?: number | null;
  fabrication_order_number?: number | null;
  /** An assembly order: the version it makes (CA, US). */
  assembly_version_id?: number | null;
  version_code?: string | null;
  /** An assembly order: sets received and given to it, what its steps can work on. */
  firmed_quantity?: number | null;
  firmed_at?: string | null;
  /** An assembly order: boxes placed on a shelf so far, every put-away added. */
  quantity_put_away?: number | null;
  /** Made from a SIGM project: its bill, split and header are locked; the floor still reports on it. */
  is_from_sigm?: boolean;
  /** The product the order makes: an assembly order's version SKU, a production order's product. */
  product_id?: number | null;
  product_sku?: string | null;
  /** Where the packed lot waits at Terrebonne. */
  storage_location_id?: number | null;
  storage_location_code?: string | null;
  sigm_location?: string | null;
  /** Computed: components followed on the floor. */
  component_count?: number | null;
  component_done_count?: number | null;
  /** Computed: share of the floor work done, 0 to 1. */
  progress?: number | null;
  /** Computed: components with parts reported missing. */
  missing_count?: number | null;
  last_event_at?: string | null;
  /** When the Cut Rite task picked up the lot's cut list. */
  cutlist_fetched_at?: string | null;
  /**
   * When the production BOM was confirmed; null while it is still being prepared.
   * Release needs it (but for a SERVICE lot or a SIGM import), and from then on
   * the lines keep their part and quantity.
   */
  materials_confirmed_at?: string | null;
}

export interface ProductionOrderMaterialDTO {
  id: number;
  production_order_id: number;
  position: number;
  level: number;
  part_id: number;
  part_number?: string | null;
  part_name?: string | null;
  stock_unit_code?: string | null;
  inventory_policy?: "TRACKED" | "UNTRACKED" | null;
  parent_part_id: number | null;
  parent_part_number?: string | null;
  operation_id: number | null;
  operation_code?: string | null;
  /** For the whole run, scrap included, in the component's stock unit. */
  quantity_required: number;
  quantity_issued?: number | null;
  quantity_remaining?: number | null;
  /** Built in the run from the lines below it; never issued from stock. */
  is_made: boolean;
  /** How the order gets the part, as its BOM said: PHANTOM or FROM_STOCK. Read only. */
  supply?: "PHANTOM" | "FROM_STOCK" | null;
  /** An assembly order's material: the box it goes in. */
  box_number?: number | null;
  planned_unit_cost: number | null;
  note: string | null;
  /**
   * Where this line is drawn from: the BOM line's consumption warehouse, else
   * the part's, else the order's. Paper laminated at VIZU is drawn at VIZU.
   */
  warehouse_id: number;
  warehouse_code?: string | null;
  /** The process at which the floor's progress consumes it (boards at CUT, foam at FOAM, cartons at PACK). */
  process_id?: number | null;
  process_code?: string | null;
  /** Sum of the office's corrections (the material log's ADJUSTED rows); the backflush respects them. */
  quantity_adjustment?: number | null;
  /** Sum of the floor's counts (panels used at the saw); null until counted. Once counted, consumption follows it. */
  quantity_counted?: number | null;
  /** Consumed in whole units (panels): consumption rounds up. */
  is_consumed_whole?: boolean;
  /** On a panel line: the virtual panel (substrate, thickness, finish) this real panel is one option of. */
  virtual_part_id?: number | null;
  virtual_part_number?: string | null;
  virtual_part_name?: string | null;
  /**
   * The engineering need: on a panel's default line the virtual panel's area in mm²
   * (0 on its alternatives); on any other line, the stock quantity.
   */
  engineering_quantity?: number | null;
  /** Appended after the production BOM was confirmed: used, but not on it. Required is 0; what was used is its adjustment. */
  is_unplanned?: boolean;
  unplanned_reason?: string | null;
}

/** A material a released lot used that its confirmed production BOM did not list. */
export interface UnplannedMaterialRequest {
  part_id: number;
  quantity: number;
  reason: string;
  /** Null: where the part is usually consumed from. */
  warehouse_id?: number | null;
}

/**
 * What the engineering BOM said against what was really consumed. A PANEL row is
 * one virtual panel, in mm² (every real panel's sheets together); a MATERIAL row
 * any other material, in its stock unit. Scrap is consumed less engineering, and
 * is stated only once the order is completed.
 */
export interface ScrapRow {
  kind: "PANEL" | "MATERIAL";
  part_id: number;
  part_number: string;
  part_name: string | null;
  /** mm2 for a panel, else the material's stock unit. */
  unit_code: string | null;
  engineering: number | null;
  consumed: number | null;
  /** Null until the order is completed. */
  scrap: number | null;
  scrap_percent: number | null;
  order_count: number;
  /** For a panel: each real panel and the sheets of it consumed. */
  real_parts: Array<{ part_id: number; part_number: string; sheets: number; area_mm2: number }> | null;
}

/** One row of a lot's material log: a correction from the office or a count from the floor. Append-only. */
export interface MaterialAdjustmentRowDTO {
  id: number;
  production_order_material_id: number;
  part_number: string;
  kind: "ADJUSTED" | "COUNTED";
  quantity: number;
  note: string | null;
  component_code: string | null;
  process_name: string | null;
  station_code: string | null;
  operator_name: string | null;
  created_by_name: string | null;
  created_at: string;
}

export interface ProductionIssueRequest {
  document_date: string | null;
  note: string | null;
  lines: Array<{ production_order_material_id: number; quantity: number }>;
}

export interface ProductionOutputRequest {
  quantity: number;
  document_date: string | null;
  note: string | null;
}

export interface ProductionPostResult {
  stock_document_id: number;
  lines: Array<{
    part_number: string;
    kind: string;
    quantity: number;
    backflushed: boolean;
    stock_movement_id: number;
  }>;
}

/**
 * Pipeline lots to turn into production orders. Each lot's Laval materials are
 * checked against what is available, lot after lot in this order; a short lot
 * stays in the pipeline unless a ROOT user overrides with a reason.
 */
export interface LotConversionRequest {
  lots: Array<{
    production_pipeline_lot_id: number;
    /** The lot's planned size when left out. */
    quantity?: number | null;
    due_date?: string | null;
    note?: string | null;
  }>;
  override_shortages?: boolean;
  override_reason?: string | null;
}

/** A Laval material a lot lacks: what it needs and what was left for it. */
export interface LotShortage {
  part_id: number;
  part_number: string;
  part_name: string | null;
  warehouse_code: string | null;
  needed: number;
  available: number;
}

/** What a conversion did (or would do, on a check): per lot, converted or short of what. */
export interface LotConversionResult {
  /** The production orders created; empty on a check. */
  orders: ProductionOrderDTO[];
  lots: Array<{
    production_pipeline_lot_id: number;
    sku: string | null;
    quantity: number;
    converted: boolean;
    overridden: boolean;
    shortages: LotShortage[];
  }>;
  /** Whether this user may convert despite shortages now. */
  override_allowed: boolean;
}

export class ProductionOrderClient extends ResourceClient<ProductionOrderDTO> {
  constructor(http: HttpClient) {
    super(http, "/production/production-order");
  }

  private action(id: number, action: string): Promise<ProductionOrderDTO> {
    return this.http.post<ProductionOrderDTO>(`${this.path}/${String(id)}/${action}`, {});
  }

  release(id: number): Promise<ProductionOrderDTO> {
    return this.action(id, "release");
  }

  complete(id: number): Promise<ProductionOrderDTO> {
    return this.action(id, "complete");
  }

  cancel(id: number): Promise<ProductionOrderDTO> {
    return this.action(id, "cancel");
  }

  reopen(id: number): Promise<ProductionOrderDTO> {
    return this.action(id, "reopen");
  }

  /** Every component made, by hand (the floor does it itself on the last step). */
  produced(id: number): Promise<ProductionOrderDTO> {
    return this.action(id, "produced");
  }

  backToProduction(id: number): Promise<ProductionOrderDTO> {
    return this.action(id, "back-to-production");
  }

  /** Consume the lot's materials for its floor progress now. */
  backflush(id: number): Promise<{ movements: number }> {
    return this.http.post<{ movements: number }>(`${this.path}/${String(id)}/backflush`, {});
  }

  /** The production BOM's panel plan: each virtual part, its real parts with their quantities, its coverage (0074). */
  panelPlans(id: number): Promise<PanelPlanDTO[]> {
    return this.http.get<PanelPlanDTO[]>(`${this.path}/${String(id)}/panel-plans`);
  }

  /** One virtual part's plan: how many of each real part (absent: none). Before confirmation. */
  setPanelPlan(id: number, virtualPartId: number, lines: Array<{ production_order_material_id: number; quantity: number }>): Promise<PanelPlanDTO[]> {
    return this.http.put<PanelPlanDTO[]>(`${this.path}/${String(id)}/panel-plan/${String(virtualPartId)}`, { lines });
  }

  /** The engineering BOMs the order was made from: its part's and every one down its phantom lines (0073). */
  engineeringBoms(id: number): Promise<OrderBomRowDTO[]> {
    return this.http.get<OrderBomRowDTO[]>(`${this.path}/${String(id)}/engineering-boms`);
  }

  /** What the lot really used of a material: more (positive) or returned (negative). */
  /** The lot's material log: corrections from the office and counts from the floor, newest first. */
  materialLog(id: number): Promise<MaterialAdjustmentRowDTO[]> {
    return this.http.get<MaterialAdjustmentRowDTO[]>(`${this.path}/${String(id)}/material-log`);
  }

  adjustMaterial(id: number, materialId: number, quantity: number, note: string | null): Promise<void> {
    return this.http.post<void>(`${this.path}/${String(id)}/material/${String(materialId)}/adjust`, { quantity, note }, { responseType: "void" });
  }

  /** The production BOM is ready: release needs it, and its lines are locked from then on. */
  confirmMaterials(id: number): Promise<ProductionOrderDTO> {
    return this.action(id, "confirm-materials");
  }

  unconfirmMaterials(id: number): Promise<ProductionOrderDTO> {
    return this.action(id, "unconfirm-materials");
  }


  /** Appends a material the lot used that was not on its production BOM, and posts the quantity as its adjustment. */
  appendUnplanned(id: number, request: UnplannedMaterialRequest): Promise<{ material_id: number }> {
    return this.http.post<{ material_id: number }>(`${this.path}/${String(id)}/material/unplanned`, request);
  }

  scrap(id: number): Promise<ScrapRow[]> {
    return this.http.get<ScrapRow[]>(`${this.path}/${String(id)}/scrap`);
  }

  /** Over every order completed between the two days, both included (YYYY-MM-DD). */
  scrapForPeriod(from: string, to: string): Promise<ScrapRow[]> {
    return this.http.get<ScrapRow[]>(`${this.path}/scrap?date_from=${encodeURIComponent(from)}&date_to=${encodeURIComponent(to)}`);
  }

  regenerate(id: number, quantity?: number): Promise<ProductionOrderDTO> {
    const query = quantity === undefined ? "" : `?quantity=${String(quantity)}`;
    return this.http.post<ProductionOrderDTO>(`${this.path}/${String(id)}/regenerate${query}`, {});
  }

  /** Whether this server turns pipeline lots into orders: off in production until cutover. */
  async lotConversionEnabled(): Promise<boolean> {
    return (await this.http.get<{ enabled: boolean }>(`${this.path}/from-lots/enabled`)).enabled;
  }

  /** What converting would do, per lot: nothing is kept. */
  checkLots(request: LotConversionRequest): Promise<LotConversionResult> {
    return this.http.post<LotConversionResult>(`${this.path}/from-lots/check`, request);
  }

  fromLots(request: LotConversionRequest): Promise<LotConversionResult> {
    return this.http.post<LotConversionResult>(`${this.path}/from-lots`, request);
  }

  issue(id: number, request: ProductionIssueRequest): Promise<ProductionPostResult> {
    return this.http.post<ProductionPostResult>(`${this.path}/${String(id)}/issue`, request);
  }

  output(id: number, request: ProductionOutputRequest): Promise<ProductionPostResult> {
    return this.http.post<ProductionPostResult>(`${this.path}/${String(id)}/output`, request);
  }
}

/** A remark on a production order, with who wrote it and when. Only its author may correct or delete it. */
export interface ProductionOrderNoteDTO {
  id: number;
  production_order_id: number;
  body: string;
  /** Read only: set from the signed-in account. */
  created_by_account_id?: number | null;
  created_by_username?: string | null;
  created_at?: string;
  updated_at?: string;
  /** Read only: the signed-in person wrote it. */
  is_mine?: boolean;
}

export class ProductionOrderNoteClient extends ResourceClient<ProductionOrderNoteDTO> {
  constructor(http: HttpClient) {
    super(http, "/production/production-order-note");
  }

  /** An order's notes, oldest first. */
  async ofOrder(orderId: number): Promise<ProductionOrderNoteDTO[]> {
    return (await this.find({ page: 0, size: 1000, filters: { production_order_id: [String(orderId)] } })).content;
  }
}

export class ProductionOrderMaterialClient extends ResourceClient<ProductionOrderMaterialDTO> {
  constructor(http: HttpClient) {
    super(http, "/production/production-order-material");
  }
}

/** A material that is taken from stock: not built in the run, not expensed when bought. */
export function isIssuable(material: ProductionOrderMaterialDTO): boolean {
  return !material.is_made && material.inventory_policy !== "UNTRACKED";
}

/* ------------------------------------------------------ assembly orders --- */

/** A production order's sets shared between its versions, one assembly order each. */
export interface AssemblySplitDTO {
  fabrication_order_id: number;
  /** What the shares must add up to: the lot, or what arrived once it closed. */
  total: number;
  /** Sets received at Terrebonne so far. */
  received: number;
  editable: boolean;
  locked_reason: string | null;
  /** When the window after the first reception ends; null before any arrived. */
  editable_until: string | null;
  lines: Array<{
    assembly_order_id: number;
    number: number;
    version_code: string | null;
    output_sku: string | null;
    quantity: number;
    firmed_quantity: number;
    status: ProductionOrderStatus;
    started: boolean;
  }>;
}

/** One step of an assembly order on the Terrebonne board. */
export interface AssemblyBoardStepDTO {
  operation_id: number;
  /** KIT, FOAM, CORNER, PACK. */
  process_code: string;
  process_name: string;
  /** The box a packing step packs. */
  box_number: number | null;
  status: "PENDING" | "ACTIVE" | "DONE" | "SKIPPED";
  quantity_required: number;
  quantity_done: number;
}

/** An assembly order on the Terrebonne board: what arrived, each step, what is ready to assemble. */
export interface AssemblyBoardRowDTO {
  order: ProductionOrderDTO;
  /** Its production order's sets on trucks and received, all versions together. */
  sets_shipped: number;
  sets_received: number;
  open_transfers: number;
  steps: AssemblyBoardStepDTO[];
  /** Sets every step before packing has passed. */
  ready: number;
  /** Units packed: the least packed box. */
  packed: number;
  box_length_inches: number | null;
  packing_minutes: number | null;
}

export class AssemblyOrderClient {
  constructor(private readonly http: HttpClient) {}

  board(): Promise<AssemblyBoardRowDTO[]> {
    return this.http.get<AssemblyBoardRowDTO[]>("/production/assembly/board");
  }

  split(fabricationOrderId: number): Promise<AssemblySplitDTO> {
    return this.http.get<AssemblySplitDTO>(`/production/assembly/split/${String(fabricationOrderId)}`);
  }

  resplit(fabricationOrderId: number, lines: Array<{ assembly_order_id: number; quantity: number }>): Promise<AssemblySplitDTO> {
    return this.http.put<AssemblySplitDTO>(`/production/assembly/split/${String(fabricationOrderId)}`, { lines });
  }

  /** What was packed since the last put-away goes on this shelf. */
  putAway(id: number, storageLocationId: number, note?: string | null): Promise<ProductionOrderDTO> {
    return this.http.post<ProductionOrderDTO>(`/production/assembly/${String(id)}/put-away`, {
      storage_location_id: storageLocationId,
      note: note ?? null,
    });
  }

  /** Ends the order by hand; what was not boxed stays short. */
  complete(id: number): Promise<ProductionOrderDTO> {
    return this.http.post<ProductionOrderDTO>(`/production/assembly/${String(id)}/complete`, {});
  }
}

/* -------------------------------------------------------------- kanban --- */

/** One kanban part at its stocking point, planned like a bought material, with the run to start. */
export interface KanbanLineDTO {
  part_id: number;
  part_number: string;
  part_name: string | null;
  warehouse_id: number;
  warehouse_code: string;
  policy: "AUTO" | "FIXED" | "ON_DEMAND";
  average_daily_usage: number;
  on_hand: number;
  /** Still to come from open kanban runs. */
  in_production: number;
  /** What open production orders still need of it. */
  reserved: number;
  position: number;
  days_of_cover: number | null;
  red: number;
  yellow: number;
  green: number;
  zone: "OUT" | "RED" | "YELLOW" | "GREEN" | "OVER";
  /** The run to start now; zero: none. */
  suggested: number;
  production_multiple: number | null;
  open_runs: number;
  note: string | null;
}

export class KanbanClient {
  constructor(private readonly http: HttpClient) {}

  plan(): Promise<KanbanLineDTO[]> {
    return this.http.get<KanbanLineDTO[]>("/production/kanban/plan");
  }

  /** Puts a run into production: a planned KANBAN order, its BOM generated. */
  run(partId: number, warehouseId: number, quantity: number): Promise<ProductionOrderDTO> {
    return this.http.post<ProductionOrderDTO>("/production/kanban/run", { part_id: partId, warehouse_id: warehouseId, quantity });
  }
}

/** One engineering BOM a production order was made from (0073). */
export interface OrderBomRowDTO {
  part_id: number;
  part_number: string;
  part_name: string;
  /** Null once that revision was deleted; the part and revision number stay. */
  bom_id: number | null;
  revision: number;
  /** When that revision was made: its label, kept when it is deleted. */
  revision_at: string | null;
  /** What that revision is now. */
  bom_status: "DRAFT" | "ACTIVE" | "ARCHIVED" | null;
  /** Still the part's active revision. */
  is_current: boolean;
  /** 0: the order's own part; 1: a part in its bill; and so on. */
  level: number;
  /** SNAPSHOT: recorded when its materials were written; BACKFILL: recorded after the fact. */
  source: "SNAPSHOT" | "BACKFILL";
  recorded_at: string;
}

/** One virtual part of a production BOM and the real parts it is cut from (0074). */
export interface PanelPlanDTO {
  virtual_part_id: number;
  virtual_number: string;
  virtual_name: string;
  kind: "PANEL" | "STRIP" | "FOAM";
  /** Net need: mm² (panel, foam) or mm (strip). */
  needed: number;
  /** What the chosen quantities cover at the expected yield, same unit. */
  planned: number;
  /** planned / needed; null when nothing is needed. */
  coverage: number | null;
  cut_job_id: number | null;
  cut_job_number: number | null;
  cut_job_status: string | null;
  lines: PanelPlanLineDTO[];
}

export interface PanelPlanLineDTO {
  production_order_material_id: number;
  part_id: number;
  number: string;
  name: string;
  unit_code: string | null;
  quantity: number;
  /** What one covers at the expected yield (mm² or mm); null when its size is unknown. */
  capacity: number | null;
  is_consumed_whole: boolean;
}
